import React, { useState, useEffect } from 'react';
import { invoiceApi } from '../api/invoice.api';
import { Invoice } from '../../shared/types';

interface InvoiceListProps {
  onLoadInvoice: (invoice: Invoice) => void;
}

const InvoiceList: React.FC<InvoiceListProps> = ({ onLoadInvoice }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);
  const [renamingId, setRenamingId] = useState<number | null>(null);

  // Fetch invoices when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchInvoices();
    }
  }, [isOpen]);

  const fetchInvoices = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await invoiceApi.getAll({ sortBy: 'createdAt', order: 'desc' });
      setInvoices(response.data);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to load invoices');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this invoice?')) {
      try {
        await invoiceApi.delete(id);
        // Refresh the list
        fetchInvoices();
      } catch (err) {
        console.error('Error deleting invoice:', err);
        setError('Failed to delete invoice');
      }
    }
  };

  const handleDuplicate = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setDuplicatingId(id);
    setError(null);
    try {
      await invoiceApi.duplicate(id);
      // Refresh the list so the new copy appears
      await fetchInvoices();
    } catch (err) {
      console.error('Error duplicating invoice:', err);
      setError('Failed to duplicate invoice');
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleRename = async (e: React.MouseEvent, invoice: Invoice) => {
    e.stopPropagation();
    if (!invoice.id) return;
    const current = invoice.name || invoice.clientName || '';
    const input = prompt('Rename invoice:', current);
    if (input === null) return; // cancelled
    const newName = input.trim();
    if (!newName || newName === invoice.name) return; // empty or unchanged
    setRenamingId(invoice.id);
    setError(null);
    try {
      // Full update preserves all other fields; only the name changes
      await invoiceApi.update(invoice.id, { ...invoice, name: newName });
      await fetchInvoices();
    } catch (err) {
      console.error('Error renaming invoice:', err);
      setError('Failed to rename invoice');
    } finally {
      setRenamingId(null);
    }
  };

  const handleLoad = (invoice: Invoice) => {
    onLoadInvoice(invoice);
    setIsOpen(false);
  };

  if (!isOpen) {
    return (
      <button onClick={() => setIsOpen(true)} className="secondary-button" style={{ marginBottom: '10px' }}>
        Manage Saved Invoices
      </button>
    );
  }

  return (
    <div className="invoice-panel-overlay" onClick={() => setIsOpen(false)}>
      <div className="invoice-side-panel" onClick={e => e.stopPropagation()}>
        <div className="invoice-panel-header">
          <h2>Saved Invoices</h2>
          <button onClick={() => setIsOpen(false)} className="close-button">&times;</button>
        </div>
        
        <div className="invoice-list">
          {isLoading ? (
            <p className="no-invoices">Loading invoices...</p>
          ) : error ? (
            <p className="no-invoices" style={{ color: '#c33' }}>{error}</p>
          ) : !invoices || invoices.length === 0 ? (
            <p className="no-invoices">No saved invoices found.</p>
          ) : (
            invoices.map((invoice) => (
              <div key={invoice.id} className="invoice-item" onClick={() => handleLoad(invoice)}>
                <div className="invoice-info">
                  <div className="invoice-name">
                    {invoice.name || invoice.clientName || 'Untitled Invoice'}
                  </div>
                  <div className="invoice-date">
                    {new Date(invoice.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="invoice-actions">
                  <button
                    onClick={(e) => handleRename(e, invoice)}
                    className="rename-button"
                    title="Rename Invoice"
                    disabled={renamingId === invoice.id}
                  >
                    {renamingId === invoice.id ? 'Renaming…' : 'Rename'}
                  </button>
                  <button
                    onClick={(e) => invoice.id && handleDuplicate(e, invoice.id)}
                    className="duplicate-button"
                    title="Duplicate Invoice"
                    disabled={duplicatingId === invoice.id}
                  >
                    {duplicatingId === invoice.id ? 'Duplicating…' : 'Duplicate'}
                  </button>
                  <button
                    onClick={(e) => invoice.id && handleDelete(e, invoice.id)}
                    className="delete-button"
                    title="Delete Invoice"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      <style>{`
        .invoice-panel-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 2000;
          display: flex;
          justify-content: flex-start;
        }
        
        .invoice-side-panel {
          background-color: white;
          width: 100%;
          max-width: 400px;
          height: 100%;
          display: flex;
          flex-direction: column;
          box-shadow: 2px 0 5px rgba(0, 0, 0, 0.2);
          animation: slideIn 0.3s ease-out;
          box-sizing: border-box;
        }

        @keyframes slideIn {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }

        .invoice-panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #eee;
          padding: 20px;
        }
        .invoice-panel-header h2 {
          margin: 0;
          font-size: 1.2rem;
          flex-grow: 1;
        }
        .close-button {
          background: none;
          border: none;
          font-size: 2rem;
          line-height: 1;
          cursor: pointer;
          color: #666;
          width: auto;
          padding: 0;
          margin-left: 10px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }
        .invoice-list {
          overflow-y: auto;
          flex: 1;
        }
        .invoice-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          border-bottom: 1px solid #eee;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        .invoice-item:hover {
          background-color: #f9f9f9;
        }
        .invoice-info {
          flex: 1;
        }
        .invoice-name {
          font-weight: bold;
          color: #333;
        }
        .invoice-date {
          font-size: 0.85rem;
          color: #666;
        }
        .invoice-actions {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .rename-button {
          background-color: #6c757d;
          color: white;
          border: none;
          padding: 5px 10px;
          border-radius: 4px;
          font-size: 0.8rem;
          width: auto;
        }
        .rename-button:hover:not(:disabled) {
          background-color: #545b62;
        }
        .rename-button:disabled {
          opacity: 0.6;
          cursor: default;
        }
        .duplicate-button {
          background-color: #4a7cff;
          color: white;
          border: none;
          padding: 5px 10px;
          border-radius: 4px;
          font-size: 0.8rem;
          width: auto;
        }
        .duplicate-button:hover:not(:disabled) {
          background-color: #2f5fd6;
        }
        .duplicate-button:disabled {
          opacity: 0.6;
          cursor: default;
        }
        .delete-button {
          background-color: #ff4444;
          color: white;
          border: none;
          padding: 5px 10px;
          border-radius: 4px;
          font-size: 0.8rem;
          width: auto;
        }
        .delete-button:hover {
          background-color: #cc0000;
        }
        .no-invoices {
          text-align: center;
          color: #666;
          padding: 20px;
        }
      `}</style>
    </div>
  );
};

export default InvoiceList;
